import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useFinancialPreferences, useUserSession } from "shared/contexts/UserContext";
import {
    buildConversionEngine,
    convertAmountWithEngine,
    convertBalancesWithEngine,
    convertDatedAmountsTotal,
    type ConversionOptions,
    type CurrencyConversionEngine,
    type DatedAmount,
} from "shared/money/currencyConversion";
import { loadBolsaHistory, getCachedBolsaHistory } from "shared/money/historicalFxRates";
import type { CurrencyBalances } from "types/Balance";

export interface CurrencyConversionContextValue {
    engine: CurrencyConversionEngine;
    historyReady: boolean;
    historyLoading: boolean;
    historyError: string | null;
    convertAmount: (amount: number, fromCurrency: string, options?: ConversionOptions) => number;
    convertBalances: (balances: CurrencyBalances, options?: ConversionOptions) => number;
    convertDatedTotal: (items: ReadonlyArray<DatedAmount>, options?: Omit<ConversionOptions, "date">) => number;
}

const CurrencyConversionContext = createContext<CurrencyConversionContextValue | null>(null);

export function CurrencyConversionProvider({ children }: PropsWithChildren) {
    const { profile } = useUserSession();
    const { userSettings } = useFinancialPreferences();
    const [historyReady, setHistoryReady] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState<string | null>(null);

    useEffect(() => {
        if (!profile.token) {
            setHistoryReady(false);
            setHistoryLoading(false);
            setHistoryError(null);
            return;
        }

        let cancelled = false;
        setHistoryLoading(true);
        setHistoryError(null);

        loadBolsaHistory()
            .then(() => {
                if (cancelled) return;
                setHistoryReady(true);
            })
            .catch((err) => {
                if (cancelled) return;
                setHistoryError(err instanceof Error ? err.message : String(err));
                setHistoryReady(true);
            })
            .finally(() => {
                if (!cancelled) setHistoryLoading(false);
            });

        return () => { cancelled = true; };
    }, [profile.token]);

    const engine = useMemo(
        () => buildConversionEngine({
            displayCurrency: userSettings.displayCurrency,
            conversionRates: userSettings.conversionRates,
            usdArsReference: userSettings.usdArsReference,
            history: historyReady ? getCachedBolsaHistory() : [],
        }),
        [
            userSettings.displayCurrency,
            userSettings.conversionRates,
            userSettings.usdArsReference,
            historyReady,
        ],
    );

    const convertAmount = useCallback(
        (amount: number, fromCurrency: string, options?: ConversionOptions) =>
            convertAmountWithEngine(engine, amount, fromCurrency, options),
        [engine],
    );

    const convertBalances = useCallback(
        (balances: CurrencyBalances, options?: ConversionOptions) =>
            convertBalancesWithEngine(engine, balances, options),
        [engine],
    );

    const convertDatedTotal = useCallback(
        (items: ReadonlyArray<DatedAmount>, options?: Omit<ConversionOptions, "date">) =>
            convertDatedAmountsTotal(engine, items, options),
        [engine],
    );

    const value = useMemo<CurrencyConversionContextValue>(() => ({
        engine,
        historyReady,
        historyLoading,
        historyError,
        convertAmount,
        convertBalances,
        convertDatedTotal,
    }), [
        engine,
        historyReady,
        historyLoading,
        historyError,
        convertAmount,
        convertBalances,
        convertDatedTotal,
    ]);

    return (
        <CurrencyConversionContext.Provider value={value}>
            {children}
        </CurrencyConversionContext.Provider>
    );
}

export function useCurrencyConversion(): CurrencyConversionContextValue {
    const ctx = useContext(CurrencyConversionContext);
    if (!ctx) {
        throw new Error("useCurrencyConversion must be used within CurrencyConversionProvider");
    }
    return ctx;
}
